import { EventArgs, EventFunction } from "./EventArgs";
import { EventObject } from "./EventObject";

/**
 * Handles the registering and triggering of events.
 * @hidden
 */
export class EventHandler<TSender, TEventType> {
    protected events: { [eventType: number]: Array<EventObject<TSender>> } = {};

    private resolveFunction: (sender: TSender, func: string) => string;
    private callingInstance: any;

    /**
     * Create a new EventHandler.
     * @param callingInstance (Optional) The instance which will be passed as "this" for scripts and functions.
     * @param resolveFunc (Optional) Function which is executed if the event needs resolving.
     */
    constructor(callingInstance?: any, resolveFunc?: (sender: TSender, func: string) => string) {
        this.callingInstance = callingInstance;
        this.resolveFunction = resolveFunc;
    }

    /**
     * Get the list of registered EventHandlers for given eventType.
     * @param eventType Type of event.
     */
    public get(eventType: TEventType): Array<EventObject<TSender>> {
        return this.events[Number(eventType)];
    }

    /**
     * Executes every function which was registered for the eventType.
     * @param sender Sender object.
     * @param eventType Type of event.
     * @param args Argument data or already created {EventArgs}.
     */
    public trigger(sender: TSender, eventType: TEventType, args?: any | EventArgs<TSender>) {
        let e: EventArgs<TSender>;
        if (args instanceof EventArgs) {
            e = <EventArgs<TSender>>args;
        } else {
            // wrap data in eventArgs
            e = new EventArgs(sender, args);
        }

        let obsoleteHandlers: EventObject<TSender>[] = [];

        let eventTypeKey = Number(eventType);
        if (this.events[eventTypeKey] != null) {
            let eventArr = this.events[eventTypeKey].slice(0);
            for (const eventObj of eventArr) {
                if (eventObj.prediction && !eventObj.prediction(sender, e)) {
                    continue;
                }
                if (eventObj.executeOnce) {
                    obsoleteHandlers.push(eventObj);
                }
                if (typeof eventObj.func == "string") {
                    // function is string
                    let func = eventObj.func;
                    if (eventObj.resolve && this.resolveFunction) {
                        // event needs resolving -> execute resolve
                        func = this.resolveFunction(sender, func);
                    }
                    this.executeScript(func, e);
                } else {
                    let eventFunc = eventObj.func; // function is a javascript function
                    eventFunc(e);
                }

                if (e.isDefaultPrevented()) {
                    break;
                }
            }
        }

        for (let hIdx = 0, hLen = obsoleteHandlers.length; hIdx < hLen; hIdx++) {
            let eventObj = obsoleteHandlers[hIdx];
            this.off(eventType, eventObj.func);
        }
    }

    /**
     * Executes function for this eventtype.
     * @param sender Sender object.
     * @param eventType Type of event.
     * @param args Arguments for the event function.
     */
    public execute<TResult = any, TArgument = any>(sender: TSender, eventType: TEventType, args?: TArgument): TResult {
        let eventTypeKey = Number(eventType);

        let obsoleteHandlers: EventObject<TSender>[] = [];
        let returnValue: TResult = null;
        if (this.events[eventTypeKey] != null) {
            let eventArr = this.events[eventTypeKey].slice(0);
            for (const eventObj of eventArr) {
                if (eventObj.executeOnce) {
                    obsoleteHandlers.push(eventObj);
                }
                if (typeof eventObj.func == "string") {
                    if (eventObj.resolve && this.resolveFunction) {
                        // event needs resolving -> execute resolve
                        eventObj.func = this.resolveFunction(sender, eventObj.func);
                    }
                    this.executeScript(eventObj.func);
                } else {
                    let e = new EventArgs(sender, args);
                    let eventFunc = eventObj.func;
                    returnValue = eventFunc(e);
                }
            }

            for (let hIdx = 0, hLen = obsoleteHandlers.length; hIdx < hLen; hIdx++) {
                let eventObj = obsoleteHandlers[hIdx];
                this.off(eventType, eventObj.func);
            }
        }
        return returnValue;
    }

    /**
     * Execute script.
     * @param script Script to be executed.
     * @param e EventArguments.
     */
    public executeScript(script: string, e?: EventArgs<TSender>): boolean {
        try {
            console.info("executing script: " + script);
            let val = new Function("e", script).call(this.callingInstance, e);
            if (e != null && val === false) {
                e.preventDefault();
            }
            return val !== false;
        } catch (error) {
            console.log("error in script: " + script + "\nerror:" + error);
            return false;
        }
    }

    /**
     * Register function for {EventType}.
     * @param eventType Type of event.
     * @param event Script, Function or EventObject.
     */
    public on(eventType: TEventType, event: string | EventFunction<TSender> | EventObject<TSender>): void {
        let eventTypeKey = Number(eventType);

        if (this.events[eventTypeKey] == null) {
            this.events[eventTypeKey] = [];
        }
        if (!(event instanceof EventObject)) {
            event = new EventObject(event);
        }
        this.events[eventTypeKey].push(event);
    }

    /**
     * Register function for {EventType} only one call.
     * @param eventType Type of event.
     * @param event Script, Function or EventObject.
     */
    public one(eventType: TEventType, event: string | EventFunction<TSender> | EventObject<TSender>): void {
        let eventTypeKey = Number(eventType);

        if (this.events[eventTypeKey] == null) {
            this.events[eventTypeKey] = [];
        }
        if (!(event instanceof EventObject)) {
            event = new EventObject(event, null, false, true);
        }
        this.events[eventTypeKey].push(event);
    }

    /**
     * Removes handlers from this EventHandler.
     * @param eventType EventType. If null, all eventhandlers are removed.
     * @param func Function or string. If null, all eventhandlers for this eventType are removed.
     */
    public off(eventType?: TEventType, func?: string | EventFunction<TSender>): void {
        if (eventType === undefined) {
            this.events = {};
            return;
        }
        let eventTypeKey = Number(eventType);
        if (func === undefined) {
            this.events[eventTypeKey] = [];
            return;
        }
        let ev = this.events[eventTypeKey];
        let arrLength: number = this.events[eventTypeKey].length;
        for (let i = 0; i < arrLength; i++) {
            if (ev[i].func == func) {
                ev.splice(i, 1);
                return;
            }
        }
    }
}
